import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PaymentMethodPicker from '../PaymentMethodPicker'
import PropTypes from 'prop-types'
import { Copy01, Clock, CheckCircle, XClose } from '@untitled-ui/icons-react'
import { MyButton, MyModal, myToaster } from '@interstellar-component'

import { BillingService } from '../../billing.service'
import { formatRp } from '../../wallet.format'
import { IS_PROD } from '@src/runtimeConfig'
import PaymentQr from '@src/components/PaymentQr'

// Figma 15206:26373 — six fixed presets. Kept as constants rather than derived
// from the balance cap: they are a product choice about round numbers, not a
// function of headroom. What headroom does is DISABLE the ones that no longer
// fit (BR-SALDO-06), which is why each is checked against it below.
const PRESETS = [500000, 1000000, 1500000, 2500000, 5000000, 10000000]

const STEP = { AMOUNT: 'amount', CONFIRM: 'confirm', VA: 'va' }

/** `12.345.678` while typing — digits only, grouped. */
function formatDigits(value) {
  const digits = String(value ?? '').replace(/\D/g, '')
  return digits ? Number(digits).toLocaleString('id-ID') : ''
}

/** `17 August 2026, 10:03`, or an em dash when the stamp is missing. */
function formatStamp(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  return `${date}, ${time}`
}

function ModalShell({ title, onClose, children, footer }) {
  return (
    // max-h + scrollable body: the method picker made this modal taller than a
    // laptop viewport, and with nothing scrollable the footer — the only way to
    // proceed or leave — sat off-screen. Header and footer stay pinned; only the
    // content scrolls.
    <div className="flex max-h-[90vh] w-[calc(100vw-32px)] max-w-[480px] flex-col overflow-hidden rounded-xl bg-white">
      <header className="flex shrink-0 items-center justify-between gap-3 px-6 pt-6">
        <h2 className="text-lg-semibold text-gray/900">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          className="flex size-8 items-center justify-center rounded-lg text-gray/400 hover:bg-gray/50"
        >
          <XClose className="size-5" />
        </button>
      </header>
      <div className="flex min-h-0 flex-col gap-4 overflow-y-auto px-6 py-5">{children}</div>
      <div className="flex shrink-0 flex-col gap-3 px-6 pb-6">{footer}</div>
    </div>
  )
}

ModalShell.propTypes = {
  title: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
  footer: PropTypes.node,
}
ModalShell.defaultProps = { children: null, footer: null }

/** One `label — value` row in the order summary. */
function SummaryRow({ label, sub, value, strong }) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-3">
      <div className="flex flex-col">
        <span className={strong ? 'text-sm-medium text-gray/900' : 'text-sm-regular text-gray/700'}>
          {label}
        </span>
        {sub ? <span className="text-xs-regular text-gray/500">{sub}</span> : null}
      </div>
      <span
        className={`shrink-0 whitespace-nowrap ${strong ? 'text-sm-semibold text-gray/900' : 'text-sm-regular text-gray/700'}`}
      >
        {value}
      </span>
    </div>
  )
}

SummaryRow.propTypes = {
  label: PropTypes.string.isRequired,
  sub: PropTypes.string,
  value: PropTypes.node.isRequired,
  strong: PropTypes.bool,
}
SummaryRow.defaultProps = { sub: null, strong: false }

/**
 * Top up saldo — Figma frames 15206:26373 → 15206:29074 → 15206:28984.
 *
 * Three steps in one component because they share the state that matters: the
 * amount chosen in step 1 is what step 2 prices and step 3 collects. Splitting
 * them would mean lifting `amount` and `invoice` into the parent for no gain.
 *
 * THE AMOUNT IS ALWAYS THE BASE. What the customer types is what lands in the
 * wallet; PPN and the gateway fee are added on top by the server and shown in
 * step 2 (BR-PPN-02). The FE never computes tax — it renders the breakdown the
 * invoice was actually created with, so the two can never disagree.
 */
function TopupFlow({ companyId, wallet, open, resumeInvoiceId, onClose, onSettled }) {
  const [step, setStep] = useState(STEP.AMOUNT)
  const [paymentMethod, setPaymentMethod] = useState(null)
  const [paymentMethodName, setPaymentMethodName] = useState('')
  const [paymentMethodFeeIdr, setPaymentMethodFeeIdr] = useState(0)
  // PPN for this amount, from the server (BR-PPN-02: the FE never derives tax).
  // Carried on each payment-method entry; 0 while BILLING_APPLY_TAX is off.
  const [paymentMethodTaxIdr, setPaymentMethodTaxIdr] = useState(0)
  // Which amount the fee in hand was priced for. The picker refetch is debounced,
  // so right after the amount changes the fee on screen answers the PREVIOUS
  // amount — freezing that into the confirm step charged a different total than
  // the one the user approved.
  const [feePricedFor, setFeePricedFor] = useState(null)
  const [amount, setAmount] = useState('')
  const [invoice, setInvoice] = useState(null)
  const [breakdown, setBreakdown] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [now, setNow] = useState(() => Date.now())

  const minTopup = Number(wallet?.min_topup_idr || 0)
  const headroom = Number(wallet?.topup_headroom_idr || 0)
  const amountIdr = Number(String(amount).replace(/\D/g, '') || 0)

  // Reset whenever the modal is reopened, so a cancelled attempt never leaks
  // its VA screen into the next one.
  useEffect(() => {
    if (!open) return
    setPaymentMethod(null)
    setPaymentMethodName('')
    setPaymentMethodFeeIdr(0)
    setPaymentMethodTaxIdr(0)
    setFeePricedFor(null)
    setAmount('')
    setInvoice(null)
    setBreakdown(null)
    setError(null)
    setBusy(false)
    // Reopened to RESUME an unpaid invoice (the "ada pembayaran belum selesai"
    // banner): skip amount/confirm and land on the VA step; the loader below
    // pulls its detail (va_number / status / expiry) so it renders like a fresh
    // create did.
    setStep(resumeInvoiceId ? STEP.VA : STEP.AMOUNT)
  }, [open, resumeInvoiceId])

  // Resume load. Kept separate from `refresh` (which closes over `invoice`,
  // still null on this reopen) — fetch the detail straight from the id.
  useEffect(() => {
    if (!open || !resumeInvoiceId) return undefined
    let active = true
    setBusy(true)
    BillingService.getInvoiceDetail(companyId, resumeInvoiceId)
      .then((res) => {
        const fresh = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res
        if (active) setInvoice(fresh)
      })
      .catch((err) => {
        if (active) myToaster(err?.response?.data || { status: 400, message: err?.message })
      })
      .finally(() => {
        if (active) setBusy(false)
      })
    return () => {
      active = false
    }
  }, [open, resumeInvoiceId, companyId])

  const amountError = useMemo(() => {
    if (!amountIdr) return null
    if (amountIdr < minTopup) return `Minimum top up Rp${minTopup.toLocaleString('id-ID')}`
    if (amountIdr > headroom) {
      return `Exceeds your remaining top-up limit (Rp${headroom.toLocaleString('id-ID')})`
    }
    return null
  }, [amountIdr, minTopup, headroom])

  const payment = invoice?.payment_detail || null
  const expiresAt = payment?.expired_at ? new Date(payment.expired_at).getTime() : null
  const isPaid = payment?.status === 'paid' || invoice?.status === 'paid'

  // One shared ticker for the countdown. Runs only while the VA step is on
  // screen and unpaid — a timer left running behind a closed modal is a leak
  // that also keeps re-rendering a component nobody is looking at.
  useEffect(() => {
    if (!open || step !== STEP.VA || isPaid || !expiresAt) return undefined
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [open, step, isPaid, expiresAt])

  const remaining = expiresAt ? Math.max(0, expiresAt - now) : null

  /**
   * A ticking clock is only useful while it is urgent. The VA window is 7 days
   * by default, and rendering that as `167:59:54` is both ugly and misleading —
   * it reads like a system about to expire. Above a day, say the days; below
   * it, count down for real.
   */
  const countdown = useMemo(() => {
    if (remaining == null) return null
    const total = Math.floor(remaining / 1000)
    if (total >= 86400) {
      const days = Math.floor(total / 86400)
      return `${days} ${days === 1 ? 'day' : 'days'} left`
    }
    const h = String(Math.floor(total / 3600)).padStart(2, '0')
    const m = String(Math.floor((total % 3600) / 60)).padStart(2, '0')
    const s = String(total % 60).padStart(2, '0')
    return Number(h) > 0 ? `${h}:${m}:${s}` : `${m}:${s}`
  }, [remaining])

  const refresh = useCallback(async () => {
    if (!invoice?.id) return
    setBusy(true)
    try {
      const res = await BillingService.getInvoiceDetail(companyId, invoice.id)
      const fresh = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res
      setInvoice(fresh)
      if (fresh?.status === 'paid') {
        myToaster({ status: 200, message: 'Payment received — your saldo has been topped up' })
        if (onSettled) onSettled()
      }
    } catch (err) {
      myToaster(err?.response?.data || { status: 400, message: err?.message })
    } finally {
      setBusy(false)
    }
  }, [companyId, invoice, onSettled])

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      const res = await BillingService.createSaldoTopup(companyId, {
        amount_idr: amountIdr,
        payment_methods: [paymentMethod],
      })
      const body = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res
      setInvoice(body?.invoice ? { ...body.invoice, payment_detail: shapePayment(body) } : null)
      setBreakdown(body?.breakdown || null)
      setStep(STEP.VA)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to create the top-up invoice')
    } finally {
      setBusy(false)
    }
  }

  const cancel = async () => {
    if (!invoice?.id) return
    setBusy(true)
    try {
      await BillingService.cancelInvoice(companyId, invoice.id)
      myToaster({ status: 200, message: 'Payment canceled' })
      onClose()
    } catch (err) {
      myToaster(err?.response?.data || { status: 400, message: err?.message })
    } finally {
      setBusy(false)
    }
  }

  const copyVa = async () => {
    const va = payment?.va_number
    if (!va) return
    try {
      await navigator.clipboard.writeText(va)
      myToaster({ status: 200, message: 'VA number copied' })
    } catch {
      myToaster({ status: 400, message: 'Copy failed — please copy it manually' })
    }
  }

  if (!open) return null

  let body = null

  if (step === STEP.AMOUNT) {
    body = (
      <ModalShell
        title="Top up saldo"
        onClose={onClose}
        footer={
          // Two equal buttons spanning the modal width (grid), primary rightmost.
          <div className="grid grid-cols-2 gap-3">
            <MyButton color="secondary" variant="outlined" size="md" type="button" expanded onClick={onClose}>
              <span className="text-sm-semibold">Cancel</span>
            </MyButton>
            <MyButton
              color="primary"
              variant="filled"
              size="md"
              type="button"
              expanded
              // Also wait for the fee to be re-priced for THIS amount — the
              // picker debounces, and Continue in that gap freezes a stale fee
              // into the total the user is about to approve.
              disabled={
                !amountIdr || !!amountError || !paymentMethod || feePricedFor !== amountIdr
              }
              onClick={() => setStep(STEP.CONFIRM)}
            >
              <span className="text-sm-semibold">Continue</span>
            </MyButton>
          </div>
        }
      >
        <div className="rounded-lg border border-gray/200">
          <div className="border-b border-gray/200 px-4 py-3">
            <span className="text-sm-medium text-gray/900">Amount</span>
          </div>
          <div className="flex flex-col gap-4 p-4">
            <div
              className={`flex items-center gap-1 rounded-lg border px-3.5 py-2.5 ${amountError ? 'border-error/300' : 'border-brand/300'
                }`}
            >
              <span className="text-md-medium text-gray/500">Rp</span>
              <input
                inputMode="numeric"
                value={formatDigits(amount)}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
                className="w-full bg-transparent text-center text-md-medium text-gray/900 focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              {PRESETS.map((p) => {
                // BR-SALDO-06: a preset that would breach the cap is dead on
                // arrival, not rejected after the click.
                const tooBig = p > headroom
                const selected = amountIdr === p
                return (
                  <button
                    key={p}
                    type="button"
                    disabled={tooBig}
                    onClick={() => setAmount(String(p))}
                    className={`rounded-full border px-2 py-1 text-sm-medium transition-colors ${selected
                        ? 'border-brand/300 bg-brand/50 text-brand/700'
                        : 'border-gray/200 bg-gray/50 text-gray/700 hover:bg-gray/100'
                      } disabled:cursor-not-allowed disabled:opacity-40`}
                    title={tooBig ? 'Exceeds your remaining top-up limit' : undefined}
                  >
                    {formatRp(p)}
                  </button>
                )
              })}
            </div>

            {amountError ? (
              <p className="text-sm-regular text-error/700">{amountError}</p>
            ) : (
              <p className="text-xs-regular text-gray/500">
                Remaining top-up limit: {formatRp(headroom)} · minimum {formatRp(minTopup)}
              </p>
            )}
            {/* After the amount block, not between it and its presets — the
                presets set the number the field holds, so they belong together. */}
            <div>
              <p className="mb-2 text-sm-medium text-gray/900">Payment method</p>
              <PaymentMethodPicker
                companyId={companyId}
                amountIdr={amountIdr || null}
                value={paymentMethod}
                onChange={(wire, m) => {
                  setPaymentMethod(wire)
                  setPaymentMethodName(m?.name || '')
                  setPaymentMethodFeeIdr(Number(m?.app_fee_idr || 0))
                  setPaymentMethodTaxIdr(Number(m?.tax_idr || 0))
                  setFeePricedFor(m ? m.priced_for_idr ?? null : null)
                }}
              />
            </div>

          </div>
        </div>
      </ModalShell>
    )
  }

  if (step === STEP.CONFIRM) {
    // Preview only. The authoritative numbers arrive with the invoice — this is
    // deliberately the same shape so the jump to step 3 is not a surprise.
    body = (
      <ModalShell
        title="Confirm payment"
        onClose={onClose}
        footer={
          <div className="grid grid-cols-2 gap-3">
            <MyButton
              color="secondary"
              variant="outlined"
              size="md"
              type="button"
              expanded
              disabled={busy}
              onClick={() => setStep(STEP.AMOUNT)}
            >
              <span className="text-sm-semibold">Back</span>
            </MyButton>
            <MyButton
              color="primary"
              variant="filled"
              size="md"
              type="button"
              expanded
              disabled={busy}
              onClick={submit}
            >
              <span className="text-sm-semibold">{busy ? 'Processing…' : 'Pay now'}</span>
            </MyButton>
          </div>
        }
      >
        <div>
          <p className="mb-2 text-sm-medium text-gray/900">Order summary</p>
          <div className="rounded-lg border border-gray/200">
            <SummaryRow label="Top up saldo" value={formatRp(amountIdr)} />
            {/* The server-priced fee for this exact method, shown BEFORE the user
                commits — the total they approve is the total on the invoice, not
                a smaller number that grows after the fact. */}
            {paymentMethodTaxIdr > 0 ? (
              <div className="border-t border-gray/200">
                <SummaryRow label="PPN" value={formatRp(paymentMethodTaxIdr)} />
              </div>
            ) : null}
            {paymentMethodFeeIdr > 0 ? (
              <div className="border-t border-gray/200">
                <SummaryRow label="App fee" value={formatRp(paymentMethodFeeIdr)} />
              </div>
            ) : null}
            <div className="border-t border-gray/200">
              <SummaryRow
                label="Total due today"
                value={formatRp(amountIdr + paymentMethodTaxIdr + paymentMethodFeeIdr)}
                strong
              />
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray/200">
          <div className="border-b border-gray/200 px-4 py-3">
            <span className="text-sm-medium text-gray/900">Payment method</span>
          </div>
          <div className="p-4">
            <div className="flex items-center justify-between gap-3 rounded-lg border border-brand/300 px-3.5 py-3">
              <span className="text-sm-medium text-gray/900">{paymentMethodName || paymentMethod}</span>
              <span className="size-4 rounded-full border-4 border-brand/600" />
            </div>
          </div>
        </div>

        <p className="text-xs-regular text-gray/500">
          Payment instructions appear after you press Pay now. Your saldo is topped up
          automatically once the payment is verified.
        </p>

        {error ? <p className="text-sm-regular text-error/700">{error}</p> : null}
      </ModalShell>
    )
  }

  if (step === STEP.VA) {
    const expired = remaining === 0 && !isPaid
    // A rendered QR (or a VA number) means the portal IS the payment UI: the
    // payer acts here, this modal is status. `inPortal` collapses both into the
    // one flag the footer + copy branch on (the gateway page becomes a dev-only
    // simulate button, not the way to pay).
    const hasQr = Boolean(payment?.qr_string || payment?.qr_url)
    const inPortal = Boolean(payment?.va_number) || hasQr
    body = (
      <ModalShell
        title="Confirm payment"
        onClose={onClose}
        footer={
          <>
            {/* With a VA on screen the portal IS the payment UI: the payer
                transfers from their bank app, this modal is status — Refresh
                leads, and the gateway's own page is a debug tool that only
                shows on a dev build (QA uses it to simulate settlement).
                A redirect-only payment (Snap e-wallet/QR) is the opposite: the
                gateway page is where paying happens, so opening it leads in
                every environment. */}
            <div className={payment?.checkout_url && (!inPortal || !IS_PROD) ? 'grid grid-cols-2 gap-3' : 'grid gap-3'}>
              <MyButton
                color={inPortal || !payment?.checkout_url ? 'primary' : 'secondary'}
                variant={inPortal || !payment?.checkout_url ? 'filled' : 'outlined'}
                size="md"
                type="button"
                expanded
                disabled={busy}
                onClick={refresh}
              >
                <span className="text-sm-semibold">{busy ? 'Checking…' : 'Refresh'}</span>
              </MyButton>
              {payment?.checkout_url && (!inPortal || !IS_PROD) ? (
                <MyButton
                  color={inPortal ? 'secondary' : 'primary'}
                  variant={inPortal ? 'outlined' : 'filled'}
                  size="md"
                  type="button"
                  expanded
                  onClick={() => window.open(payment.checkout_url, '_blank', 'noopener')}
                >
                  <span className="text-sm-semibold">
                    {inPortal ? 'Simulate payment (dev)' : 'Open payment page'}
                  </span>
                </MyButton>
              ) : null}
            </div>
            {!isPaid ? (
              <button
                type="button"
                disabled={busy}
                onClick={cancel}
                className="w-full rounded-lg border border-error/300 px-3.5 py-2.5 text-sm-semibold text-error/700 hover:bg-error/50 disabled:opacity-50"
              >
                Cancel payment
              </button>
            ) : null}
          </>
        }
      >
        <div className="flex items-center justify-between gap-3 rounded-lg border border-gray/200 p-4">
          <div className="flex items-center gap-3">
            <span
              className={`flex size-10 items-center justify-center rounded-full ${isPaid ? 'bg-success/100 text-success/600' : 'bg-warning/100 text-warning/600'
                }`}
            >
              {isPaid ? <CheckCircle className="size-5" /> : <Clock className="size-5" />}
            </span>
            <div>
              <p className="text-md-semibold text-gray/900">
                {isPaid ? 'Payment received' : 'Pay before'}
              </p>
              {/* Once paid, the deadline is history — showing it reads as a
                  future obligation. Switch to when it was actually paid. */}
              <p className="text-sm-regular text-gray/600">
                {formatStamp(isPaid ? payment?.paid_at || invoice?.paid_at : payment?.expired_at)}
              </p>
            </div>
          </div>
          {!isPaid && countdown ? (
            <span className="shrink-0 rounded-full border border-warning/300 px-2 py-0.5 text-xs-medium text-warning/700">
              {countdown}
            </span>
          ) : null}
        </div>

        {/* The QR itself — the payment instruction, in-portal. Hidden once paid
            (nothing left to scan) or expired (a dead QR invites a failed scan). */}
        {hasQr && !isPaid && !expired ? (
          <PaymentQr qrString={payment?.qr_string} qrUrl={payment?.qr_url} />
        ) : null}

        <div className="rounded-lg border border-gray/200">
          <div className="flex items-center justify-between border-b border-gray/200 px-4 py-3">
            <span className="text-sm-medium text-gray/900">Payment status</span>
            <span
              className={`rounded-md border px-2 py-0.5 text-xs-medium ${isPaid
                  ? 'border-success/200 bg-success/50 text-success/700'
                  : expired
                    ? 'border-gray/300 bg-gray/100 text-gray/700'
                    : 'border-warning/200 bg-warning/50 text-warning/700'
                }`}
            >
              {isPaid ? 'Paid' : expired ? 'Expired' : 'Unpaid'}
            </span>
          </div>
          <SummaryRow
            label="Payment method"
            value={
              payment?.va_bank
                ? `Virtual Account ${payment.va_bank}`
                : hasQr
                  ? 'QRIS'
                  : // On resume the picker never ran, so paymentMethodName is empty;
                  // fall back to the channel/method the invoice detail carries so a
                  // real payment never renders as a bare dash.
                  paymentMethodName || payment?.payment_channel || payment?.payment_method || '—'
            }
          />
          {/* Only when a VA actually exists. A GoPay/Snap payment has none, and a
              dash with a copy icon is an affordance for copying a dash. */}
          {payment?.va_number ? (
            <div className="flex items-center justify-between gap-4 border-t border-gray/200 px-4 py-3">
              <span className="text-sm-regular text-gray/700">VA number</span>
              <button
                type="button"
                onClick={copyVa}
                className="flex items-center gap-2 text-sm-semibold text-gray/900 hover:text-brand/700"
              >
                {payment.va_number}
                <Copy01 className="size-4 text-brand/600" />
              </button>
            </div>
          ) : null}
          <div className="border-t border-gray/200">
            <SummaryRow
              label="Total amount"
              value={formatRp(breakdown?.total_idr ?? invoice?.total_idr)}
              strong
            />
          </div>
          {/* The two numbers differ whenever PPN or a gateway fee applies, and
              that difference is exactly what confuses people — so name it. */}
          {breakdown && Number(breakdown.credited_idr) !== Number(breakdown.total_idr) ? (
            <div className="border-t border-gray/200">
              <SummaryRow
                label="Saldo credited"
                sub="PPN and the app fee are charged separately and are not added to your saldo"
                value={formatRp(breakdown.credited_idr)}
              />
            </div>
          ) : null}
        </div>

        <div className="rounded-lg border border-gray/200 p-4">
          <p className="text-sm-medium text-gray/900">Important</p>
          <ul className="mt-1 list-disc pl-5 text-sm-regular text-gray/600">
            {payment?.va_number ? (
              <li>Virtual Account transfers must be made from the selected bank only.</li>
            ) : hasQr ? (
              <li>Scan the QR above with any bank or e-wallet app that supports QRIS.</li>
            ) : (
              <li>Complete the payment on the payment page ({paymentMethodName || 'selected method'}).</li>
            )}
            <li>Your saldo is topped up automatically once the payment is verified.</li>
          </ul>
        </div>
      </ModalShell>
    )
  }

  return <MyModal open={open} onClose={onClose} forceBlur children={body} />
}

/**
 * The create response returns invoice + payment as siblings; the detail endpoint
 * returns a nested `payment_detail`. Normalise on the way in so the VA step
 * reads one shape whether it just created the invoice or just refreshed it.
 */
function shapePayment(body) {
  const p = body?.payment
  if (!p) return null
  return {
    payment_id: p.id,
    status: p.status,
    va_number: p.va_number || null,
    va_bank: p.va_bank || null,
    qr_string: p.qr_string || null,
    qr_url: p.qr_url || null,
    expired_at: p.expired_at || null,
    checkout_url: body?.payment_url || null,
    paid_at: p.paid_at || null,
  }
}

TopupFlow.propTypes = {
  companyId: PropTypes.string,
  wallet: PropTypes.shape({
    min_topup_idr: PropTypes.number,
    topup_headroom_idr: PropTypes.number,
  }),
  open: PropTypes.bool,
  // When set on open, resume this unpaid invoice at the VA step instead of
  // starting a new top-up from the amount step.
  resumeInvoiceId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSettled: PropTypes.func,
}

TopupFlow.defaultProps = {
  companyId: null,
  wallet: null,
  open: false,
  resumeInvoiceId: null,
  onSettled: null,
}

export default TopupFlow
